import { queryOptions, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import {
  type ConversationGroupType,
  type ConversationSessionsTypeUnion,
  type ConversationSessionType,
  type SessionDataGroupType,
  type SessionDataResponseType,
  type SessionDataSessionType,
  type SessionDataTypeUnion,
  type SessionListResponseType,
  sessionDataResponseSchema,
  sessionListResponseSchema,
} from '@/models/conversations/conversation-session';
import { generateResponseScheme, type RespondedWith } from '@/models/response';
import { promisifySessionsHydration, useConversationSessionStore } from '@/stores/conversation-session';
import { addMessages } from '@/stores/message';
import { fetcher } from '@/utils/fetcher';
import { transformMessage } from '@/utils/message';
import { queryClient } from '@/utils/query-client';
import { generateSessionKey, sortSessions } from '@/utils/session';

interface SessionInfoType {
  SessionType: '1' | '2';
  ID: number;
  FromID: number;
}

function generateSessionInfos(
  remoteSessions: SessionListResponseType,
  storedSessions: ConversationSessionsTypeUnion[] = [],
) {
  return [...remoteSessions.ChatSessions, ...remoteSessions.Groups].reduce<SessionInfoType[]>(
    (sessionInfos, remoteSession) => {
      const sessionType = remoteSession.SessionType === 'session' ? '1' : '2';
      const storedSession = storedSessions.find(
        (s) => s.SessionType === remoteSession.SessionType && s.ID === remoteSession.ID,
      );
      const lastReadId =
        remoteSession.SessionType === 'session' ? remoteSession.ReadChatID : remoteSession.ReadGroupChatID;

      if (!storedSession || remoteSession.IsNewSession) {
        sessionInfos.push({
          SessionType: sessionType,
          ID: remoteSession.ID,
          FromID: 0,
        });
      } else if (lastReadId < remoteSession.LastMsgID) {
        sessionInfos.push({
          SessionType: sessionType,
          ID: remoteSession.ID,
          FromID: lastReadId,
        });
      }

      return sessionInfos;
    },
    [],
  );
}

function mergeSessionResponses(
  listResponse: SessionListResponseType,
  dataResponse: SessionDataResponseType,
  storedSessions: ConversationSessionsTypeUnion[] = [],
): ConversationSessionsTypeUnion[] {
  const dataSessionMap = [...dataResponse.ChatSessions, ...dataResponse.Groups].reduce<
    Record<string, SessionDataTypeUnion>
  >((accu, session) => {
    accu[generateSessionKey(session.SessionType, session.ID)] = session;
    return accu;
  }, {});

  const storedSessionMap = storedSessions.reduce(
    (accu, session) => {
      accu[generateSessionKey(session.SessionType, session.ID)] = session;
      return accu;
    },
    {} as Record<string, ConversationSessionsTypeUnion>,
  );

  return [
    ...listResponse.ChatSessions.map<ConversationSessionType>((session) => {
      const key = generateSessionKey(session.SessionType, session.ID);
      const data = dataSessionMap[key] as SessionDataSessionType;
      const stored = storedSessionMap[key];
      const shouldUseStoredSession = getShouldUseStoredSession(stored, data);

      return {
        ...session,
        UnReadCount: (shouldUseStoredSession ? stored.UnReadCount : data.UnReadCount) ?? 0,
        UnReadMaxID: (shouldUseStoredSession ? stored.UnReadMaxID : data.UnReadMaxID) ?? 0,
        IsNewSession: stored?.IsNewSession === undefined ? session.IsNewSession : stored.IsNewSession,
        Chats: data?.Chats ?? [],
      };
    }),
    ...listResponse.Groups.map<ConversationGroupType>((session) => {
      const key = generateSessionKey(session.SessionType, session.ID);
      const data = dataSessionMap[key] as SessionDataGroupType;
      const stored = storedSessionMap[key];
      const shouldUseStoredSession = getShouldUseStoredSession(stored, data);

      return {
        ...session,
        UnReadCount: (shouldUseStoredSession ? stored.UnReadCount : data.UnReadCount) ?? 0,
        UnReadMaxID: (shouldUseStoredSession ? stored.UnReadMaxID : data.UnReadMaxID) ?? 0,
        IsNewSession: stored?.IsNewSession === undefined ? session.IsNewSession : stored.IsNewSession,
        GotAt: (session.ReadGroupChatID < session.LastMsgID ? data?.GotAt : false) ?? false,
        Chats: data?.Chats ?? [],
      };
    }),
  ];
}

function getShouldUseStoredSession(
  stored: ConversationSessionsTypeUnion | undefined,
  remote: SessionDataTypeUnion | undefined,
): stored is ConversationSessionsTypeUnion {
  if (!stored) return false;
  if (!remote) return true;
  if (stored.LastMsgID !== remote.LastMsgID) {
    return stored.LastMsgID > remote.LastMsgID;
  }
  return stored.UnReadCount >= remote.UnReadCount;
}

async function getAllSessionList(signal: AbortSignal) {
  const response = await fetcher<RespondedWith<SessionListResponseType>>('/GetAllSessionList', undefined, {
    signal,
    schema: generateResponseScheme(sessionListResponseSchema),
    validResponseCodes: [0, 1],
  });

  return response?.Data;
}

async function getAllSessionData(sessionInfos: SessionInfoType[], signal: AbortSignal) {
  if (sessionInfos.length === 0)
    return {
      ChatSessions: [],
      Groups: [],
    };

  const response = await fetcher<RespondedWith<SessionDataResponseType>>(
    '/GetAllSessionData',
    { SessionInfos: sessionInfos },
    {
      signal,
      schema: generateResponseScheme(sessionDataResponseSchema),
      validResponseCodes: [0, 1],
    },
  );

  return response?.Data;
}

export const allChatSessionQueryOptions = queryOptions({
  queryKey: ['/GetAllChatSession'],
  queryFn: async ({ signal, queryKey }): Promise<ConversationSessionsTypeUnion[]> => {
    await promisifySessionsHydration();

    const cachedData = queryClient.getQueryData<ConversationSessionsTypeUnion[]>(queryKey) ?? [];

    const sessionList = await getAllSessionList(signal);

    if (!sessionList) return cachedData;

    const sessionInfos = generateSessionInfos(sessionList, cachedData);

    if (sessionInfos.length === 0) {
      return mergeSessionResponses(sessionList, { ChatSessions: [], Groups: [] }, cachedData);
    }

    const sessionData = await getAllSessionData(sessionInfos, signal);

    if (!sessionData) return cachedData;

    const data = mergeSessionResponses(sessionList, sessionData, cachedData);

    const { unhideSession } = useConversationSessionStore.getState();
    const messages: ConversationMessageTypeUnion[] = [];
    for (const session of data) {
      messages.push(...(session.Chats ?? []).map(transformMessage));
      const key = generateSessionKey(session.SessionType, session.ID);
      const cachedSession = cachedData.find((s) => s.SessionType === session.SessionType && s.ID === session.ID);
      if (cachedSession && session.LastMsgID > cachedSession.LastMsgID) {
        unhideSession(key);
      }
    }

    addMessages(messages).catch(console.warn);

    return data;
  },
  placeholderData: [],
  staleTime: 0,
});

export type SessionFilterType = 'all' | 'unread' | 'groups';

export const sessionFilterOptions: Array<{ value: SessionFilterType; label: string }> = [
  { value: 'all', label: 'conversation.list.filter.all' },
  { value: 'unread', label: 'conversation.list.filter.unread' },
  { value: 'groups', label: 'conversation.list.filter.groups' },
];

export default function useConversationSessions() {
  const { hiddenSessions } = useConversationSessionStore();

  const query = useQuery({
    ...allChatSessionQueryOptions,
    select: (data): ConversationSessionsTypeUnion[] => {
      if (!data) return [];

      return sortSessions(
        data.filter(({ SessionType, ID }) => !hiddenSessions.includes(generateSessionKey(SessionType, ID))),
      );
    },
  });

  return {
    availableSessions: query.data ?? [],
    ...query,
  };
}

export function useFilteredSessions(initialFilter: SessionFilterType = 'all') {
  const [filter, setFilter] = useState<SessionFilterType>(initialFilter);
  const { hiddenSessions } = useConversationSessionStore();

  const query = useQuery({
    ...allChatSessionQueryOptions,
    select: (data): ConversationSessionsTypeUnion[] => {
      if (!data) return [];

      const sessions = data.filter((session) => {
        if (hiddenSessions.includes(generateSessionKey(session.SessionType, session.ID))) return false;

        if (filter === 'all') return true;
        if (filter === 'unread') return session.UnReadCount > 0;
        if (filter === 'groups') return session.SessionType === 'group';

        return false;
      });

      return sortSessions(sessions);
    },
  });

  return {
    filter,
    setFilter,
    availableSessions: query.data ?? [],
    ...query,
  };
}

export function useSessionUnreadCount(shouldAnnounce = false) {
  const { dispatch } = useCommDispatcher();
  const { hiddenSessions } = useConversationSessionStore();

  const query = useQuery({
    ...allChatSessionQueryOptions,
    select: (data): number => {
      if (!data) return 0;

      return data.reduce((sum, session) => {
        const key = generateSessionKey(session.SessionType, session.ID);

        if (session.IsNotDisturb || hiddenSessions.includes(key)) return sum;

        let delta = 0;
        if (session.IsNewSession) delta = 1;
        if (session.UnReadCount === Number.POSITIVE_INFINITY) delta = 1;
        else if (session.UnReadCount > 0) delta = session.UnReadCount;
        if (session.SessionType === 'group' && session.GroupJoinApplyCount > 0) delta += session.GroupJoinApplyCount;

        return sum + delta;
      }, 0);
    },
  });

  useEffect(() => {
    if (!shouldAnnounce) return;

    dispatch({
      event: 'new-message',
      data: { count: query.data ?? 0 },
    });
  }, [query.data, dispatch, shouldAnnounce]);

  return {
    unreadCount: query.data ?? 0,
    ...query,
  };
}
